import { useEffect, useRef, useState } from "react";

/**
 * The landing demo is a full canvas inside a scrolling page. Until the visitor
 * clicks or taps the desk, plain wheel and one-finger swipes scroll the page;
 * pinch and Ctrl/⌘-wheel still zoom the drawing. Leaving the desk, clicking
 * elsewhere or scrolling it out of view hands scrolling back to the page.
 */
export function wheelGoesToPage(
  engaged: boolean,
  event: Pick<WheelEvent, "ctrlKey" | "metaKey">,
) {
  return !engaged && !event.ctrlKey && !event.metaKey;
}

const hintMs = 1600;

export function useDeskScrollGuard() {
  const ref = useRef<HTMLDivElement>(null);
  const [engaged, setEngaged] = useState(false);
  const [hint, setHint] = useState(false);
  const engagedRef = useRef(engaged);
  engagedRef.current = engaged;

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    let hintTimer = 0;
    const onWheel = (event: WheelEvent) => {
      if (!wheelGoesToPage(engagedRef.current, event)) return;
      // Capture phase on the wrapper: the canvas never sees the event, so it
      // does not call preventDefault and the page scrolls natively.
      event.stopPropagation();
      setHint(true);
      window.clearTimeout(hintTimer);
      hintTimer = window.setTimeout(() => setHint(false), hintMs);
    };
    const onPointerDown = (event: PointerEvent) => {
      if (el.contains(event.target as Node)) {
        setEngaged(true);
        setHint(false);
      } else setEngaged(false);
    };
    const onPointerLeave = (event: PointerEvent) => {
      // Touch has no hover; a tap elsewhere or scrolling away releases it.
      if (event.pointerType === "mouse" && event.buttons === 0)
        setEngaged(false);
    };
    const observer =
      typeof IntersectionObserver === "function"
        ? new IntersectionObserver(
            ([entry]) => {
              if (entry && entry.intersectionRatio < 0.35) setEngaged(false);
            },
            { threshold: [0, 0.35] },
          )
        : null;
    observer?.observe(el);
    el.addEventListener("wheel", onWheel, { capture: true, passive: true });
    el.addEventListener("pointerleave", onPointerLeave);
    document.addEventListener("pointerdown", onPointerDown, true);
    return () => {
      window.clearTimeout(hintTimer);
      observer?.disconnect();
      el.removeEventListener("wheel", onWheel, { capture: true });
      el.removeEventListener("pointerleave", onPointerLeave);
      document.removeEventListener("pointerdown", onPointerDown, true);
    };
  }, []);

  return { ref, engaged, hint };
}
